import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

export const ProfileSetup = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    gender: user?.gender || '',
    bio: user?.bio || '',
    interests: user?.interests || [],
    preferences: user?.preferences || { min_age: 18, max_age: 50, gender: '' }
  });
  const [photos, setPhotos] = useState(user?.photos || []);
  const [interestInput, setInterestInput] = useState('');

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    maxFiles: 6,
    onDrop: async (acceptedFiles) => {
      for (const file of acceptedFiles) {
        if (photos.length >= 6) {
          toast.error('Maximum 6 photos allowed');
          break;
        }
        await uploadPhoto(file);
      }
    }
  });

  const uploadPhoto = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/upload/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotos([...photos, response.data.url]);
      toast.success('Photo uploaded!');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const addInterest = () => {
    if (interestInput && !formData.interests.includes(interestInput)) {
      setFormData({ ...formData, interests: [...formData.interests, interestInput] });
      setInterestInput('');
    }
  };

  const removeInterest = (interest) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(i => i !== interest)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/profile', {
        ...formData,
        photos,
        age: parseInt(formData.age)
      });
      updateUser(response.data);
      toast.success('Profile updated!');
      navigate('/discover');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 p-6 pb-24" data-testid="profile-setup-page">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-testid="setup-title">Set Up Your Profile</h1>
          <p className="text-slate-600 mt-2">Let's get to know you better</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="p-6 rounded-2xl" data-testid="photo-upload-section">
            <h3 className="font-semibold text-lg mb-4">Photos</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                  <img src={photo} alt={`Profile ${index}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {photos.length < 6 && (
              <div {...getRootProps()} className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-electric-coral transition-colors" data-testid="photo-dropzone">
                <input {...getInputProps()} />
                <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-600">Drag photos or click to upload</p>
                <p className="text-xs text-slate-400 mt-1">Up to 6 photos</p>
              </div>
            )}
          </Card>

          <Card className="p-6 rounded-2xl space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl mt-1"
                required
                data-testid="name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Age</label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  className="rounded-xl mt-1"
                  min="18"
                  max="100"
                  required
                  data-testid="age-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Gender</label>
                <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="gender-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Bio</label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="rounded-xl mt-1 min-h-[100px]"
                placeholder="Tell us about yourself..."
                data-testid="bio-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Interests</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={interestInput}
                  onChange={(e) => setInterestInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                  className="rounded-xl"
                  placeholder="Add interest"
                  data-testid="interest-input"
                />
                <Button type="button" onClick={addInterest} className="rounded-xl" data-testid="add-interest-button">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.interests.map((interest, idx) => (
                  <span
                    key={idx}
                    className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                  >
                    {interest}
                    <button type="button" onClick={() => removeInterest(interest)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-2xl">
            <h3 className="font-semibold text-lg mb-4">Preferences</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Looking for</label>
                <Select
                  value={formData.preferences.gender}
                  onValueChange={(value) => setFormData({ ...formData, preferences: { ...formData.preferences, gender: value } })}
                >
                  <SelectTrigger className="rounded-xl mt-1" data-testid="preference-gender-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Men</SelectItem>
                    <SelectItem value="female">Women</SelectItem>
                    <SelectItem value="everyone">Everyone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Min Age</label>
                  <Input
                    type="number"
                    value={formData.preferences.min_age}
                    onChange={(e) => setFormData({ ...formData, preferences: { ...formData.preferences, min_age: parseInt(e.target.value) } })}
                    className="rounded-xl mt-1"
                    min="18"
                    data-testid="min-age-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Max Age</label>
                  <Input
                    type="number"
                    value={formData.preferences.max_age}
                    onChange={(e) => setFormData({ ...formData, preferences: { ...formData.preferences, max_age: parseInt(e.target.value) } })}
                    className="rounded-xl mt-1"
                    max="100"
                    data-testid="max-age-input"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Button
            type="submit"
            className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
            disabled={loading}
            data-testid="submit-profile-button"
          >
            {loading ? 'Saving...' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
};